import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    restrictToVerticalAxis,
    restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import './EmergencyRouteList.css';
import { TEXT_EMERGENCY_ROUTE_LIST, TEXT_FILE_NAME, TEXT_ROUTE_NAME, TEXT_DISPLAY_ORDER, TEXT_DISPLAY_COLOR, TEXT_SAVE } from 'constants/message';
import EmergencyRouteItem from './EmergencyRouteItem';

const propTypes = {
    items: PropTypes.array,
    onDelete: PropTypes.func,
    onSave: PropTypes.func,
    onColorChange: PropTypes.func,
    onReorder: PropTypes.func,
    dragDisabled: PropTypes.bool,
};

const defaultProps = {
    items: [],
    onDelete: (item) => { },
    onSave: () => { },
    onColorChange: (index, color) => { },
    onReorder: (items) => { },
    dragDisabled: false,
};

const EmergencyRouteList = (props) => {
    const { items: propItems, onReorder, dragDisabled } = props;
    const [items, setItems] = useState([]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum distance to start dragging
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        // Ensure each item has a unique id
        const itemsWithIds = propItems.map((item, index) => ({
            ...item,
            id: item.id || `route-${index}-${item.roadID || index}`,
        }));
        setItems(itemsWithIds);
    }, [propItems]);

    const handleDragEnd = (event) => {
        if (dragDisabled) {
            return;
        }

        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            const reorderedItems = arrayMove(items, oldIndex, newIndex);

            // Update display order
            const updatedItems = reorderedItems.map((item, i) => ({
                ...item,
                displayOrder: i + 1,
            }));

            setItems(updatedItems);
            onReorder(updatedItems);
        }
    };

    const renderHeader = () => {
        return (
            <div className="emergency_route_list_header_item_wrapper">
                <div className="emergency_route_list_header_item">
                    <div className="drag_handle_header_space">
                        {/* Empty space for drag handle */}
                    </div>
                    <div className="emergency_route_list_filename_header_cell">
                        {TEXT_FILE_NAME}
                    </div>
                    <div className="emergency_route_list_routename_header_cell">
                        {TEXT_ROUTE_NAME}
                    </div>
                    <div className="emergency_route_list_order_header_cell">
                        {TEXT_DISPLAY_ORDER}
                    </div>
                    <div className="emergency_route_list_color_header_cell">
                        {TEXT_DISPLAY_COLOR}
                    </div>
                    <div className="emergency_route_list_action_cell"></div>
                </div>
                <div className="emergency_route_list_separator" />
            </div>
        );
    };

    const renderList = () => {
        return (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
            >
                <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
                    <div className="emergency_route_list_items_container">
                        {renderHeader()}
                        {items.map((item, index) => (
                            <EmergencyRouteItem
                                key={item.id}
                                item={item}
                                index={index}
                                onDelete={props.onDelete}
                                onColorChange={props.onColorChange}
                                dragDisabled={dragDisabled}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
        );
    };

    return (
        <div className="emergency_route_list_container">
            <div className="emergency_route_list_header">
                <label className="emergency_route_list_title">{TEXT_EMERGENCY_ROUTE_LIST}</label>
            </div>
            <div className="emergency_route_list_content">
                {renderList()}
            </div>
            <div className="emergency_route_list_actions">
                <button className="button_active" onClick={props.onSave}>
                    {TEXT_SAVE}
                </button>
            </div>
        </div>
    );
};

EmergencyRouteList.propTypes = propTypes;
EmergencyRouteList.defaultProps = defaultProps;
export default EmergencyRouteList;
